import type { ComponentType, SVGProps } from 'react';
import {
  BellAlertIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  HeartIcon,
  HomeIcon,
  PaintBrushIcon,
  ShoppingBagIcon,
  SparklesIcon,
  TruckIcon,
  UserGroupIcon,
  UserIcon,
  BuildingStorefrontIcon,
  LinkIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { CREATOR_PATHS, isCreatorStudioPath } from './creatorProgramPaths';
import { hasCreatorPortalRole } from '../utils/creatorPortalAccess';
import { ACCOUNT_WISHLIST_PATH } from './accountPaths';
import { SELLER_SETTINGS_HREF } from '../components/sidebar/sellerNavigation';
import { IUser, UserRole } from '../types/auth.types';
import { hasStaffPortalRole } from '../utils/staffPortalAccess';
import {
  Permission,
  hasAnyPermission,
  hasPermission,
} from '../utils/permissions';

export type NavPersona = 'owner' | 'staff' | 'seller' | 'creator' | 'buyer';

export type UserNavMenuItem = {
  label: string;
  description: string;
  path: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
};

export type NavRouteZone = 'storefront' | 'account' | 'checkout' | 'work';

export type UserNavMenuConfig = {
  persona: NavPersona;
  personaLabel: string;
  greeting: string;
  tagline: string;
  spotlightLabel: string;
  spotlightDescription: string;
  spotlightPath: string;
  hubLabel: string;
  hubDescription: string;
  hubPath: string;
  profileLabel: string;
  profileDescription: string;
  profilePath: string;
  items: UserNavMenuItem[];
  accent: 'buyer' | 'seller' | 'creator' | 'owner' | 'staff';
};

const STAFF_ROLE_PRIORITY: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.DEPARTMENT_HEAD,
  UserRole.SPECIALIST,
  UserRole.SUPPORT,
];

const STAFF_ROLE_LABELS: Partial<Record<UserRole, string>> = {
  [UserRole.SUPER_ADMIN]: 'Super admin',
  [UserRole.ADMIN]: 'Admin',
  [UserRole.DEPARTMENT_HEAD]: 'Department head',
  [UserRole.SPECIALIST]: 'Specialist',
  [UserRole.SUPPORT]: 'Support',
};

export function resolveNavPersona(
  roles: UserRole[] | undefined,
  pathname?: string,
): NavPersona {
  if (!roles?.length) return 'buyer';

  if (pathname && isCreatorStudioPath(pathname) && hasCreatorPortalRole(roles)) {
    return 'creator';
  }
  if (pathname?.startsWith('/seller') && roles.includes(UserRole.SELLER)) {
    return 'seller';
  }
  if (pathname?.startsWith('/owner') && (roles.includes(UserRole.OWNER) || hasStaffPortalRole(roles))) {
    return roles.includes(UserRole.OWNER) ? 'owner' : 'staff';
  }

  if (roles.includes(UserRole.OWNER)) return 'owner';
  if (hasStaffPortalRole(roles)) return 'staff';
  if (roles.includes(UserRole.SELLER)) return 'seller';
  if (hasCreatorPortalRole(roles)) return 'creator';
  return 'buyer';
}

export function resolveRouteZone(pathname: string): NavRouteZone {
  if (pathname.startsWith('/account')) return 'account';
  if (pathname.startsWith('/cart') || pathname.startsWith('/checkout')) {
    return 'checkout';
  }
  if (
    pathname.startsWith('/seller') ||
    isCreatorStudioPath(pathname) ||
    pathname.startsWith('/owner')
  ) {
    return 'work';
  }
  return 'storefront';
}

export function getPersonaLabel(persona: NavPersona, roles: UserRole[]): string {
  switch (persona) {
    case 'owner':
      return 'Owner';
    case 'staff': {
      for (const role of STAFF_ROLE_PRIORITY) {
        if (roles.includes(role)) {
          return STAFF_ROLE_LABELS[role] ?? 'Team member';
        }
      }
      return 'Team member';
    }
    case 'seller':
      return 'Seller';
    case 'creator':
      return 'Creator';
    case 'buyer':
    default:
      return 'Buyer';
  }
}

export function getTimeGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const PERSONA_COPY: Record<
  NavPersona,
  Pick<
    UserNavMenuConfig,
    | 'greeting'
    | 'tagline'
    | 'spotlightLabel'
    | 'spotlightDescription'
    | 'spotlightPath'
  >
> = {
  buyer: {
    greeting: getTimeGreeting(),
    tagline: 'Your drops, your designs — all in one place.',
    spotlightLabel: 'Shop new drops',
    spotlightDescription: 'Fresh styles added weekly',
    spotlightPath: '/shop',
  },
  seller: {
    greeting: getTimeGreeting(),
    tagline: 'Your store is live. Keep the momentum going.',
    spotlightLabel: 'Open design studio',
    spotlightDescription: 'Create your next bestseller',
    spotlightPath: '/seller/design-tool',
  },
  creator: {
    greeting: getTimeGreeting(),
    tagline: 'Grow your audience. Earn from every referral.',
    spotlightLabel: 'Copy referral link',
    spotlightDescription: 'Share your storefront',
    spotlightPath: CREATOR_PATHS.links,
  },
  owner: {
    greeting: getTimeGreeting(),
    tagline: 'Platform pulse — revenue, ops, and growth.',
    spotlightLabel: 'Review alerts',
    spotlightDescription: 'Issues that need your eye',
    spotlightPath: '/owner/alerts/operational',
  },
  staff: {
    greeting: getTimeGreeting(),
    tagline: 'Clear the queue. Delight every customer.',
    spotlightLabel: 'Order queue',
    spotlightDescription: 'Process what’s waiting',
    spotlightPath: '/owner/orders',
  },
};

const BUYER_MENU: Omit<UserNavMenuConfig, 'persona' | 'personaLabel' | keyof typeof PERSONA_COPY.buyer> = {
  hubLabel: 'My account',
  hubDescription: 'Orders, designs & saved pieces',
  hubPath: '/account',
  profileLabel: 'Profile & settings',
  profileDescription: 'Personal details & preferences',
  profilePath: '/account/settings',
  accent: 'buyer',
  items: [
    {
      label: 'Dashboard',
      description: 'Overview of your activity',
      path: '/account/dashboard',
      icon: HomeIcon,
    },
    {
      label: 'Orders',
      description: 'Track deliveries & returns',
      path: '/account/orders',
      icon: ClipboardDocumentListIcon,
    },
    {
      label: 'My designs',
      description: 'Creations you saved or made',
      path: '/account/designs',
      icon: SparklesIcon,
    },
    {
      label: 'Wishlist',
      description: 'Pieces you want to grab later',
      path: ACCOUNT_WISHLIST_PATH,
      icon: HeartIcon,
    },
  ],
};

const CREATOR_MENU: Omit<UserNavMenuConfig, 'persona' | 'personaLabel' | keyof typeof PERSONA_COPY.creator> = {
  hubLabel: 'Creator Studio',
  hubDescription: 'Commissions, links & performance',
  hubPath: CREATOR_PATHS.dashboard,
  profileLabel: 'Creator settings',
  profileDescription: 'Profile, payouts & storefront',
  profilePath: CREATOR_PATHS.settings,
  accent: 'creator',
  items: [
    {
      label: 'Overview',
      description: 'Earnings snapshot & goals',
      path: CREATOR_PATHS.dashboard,
      icon: HomeIcon,
    },
    {
      label: 'Links & codes',
      description: 'Referral links to share',
      path: CREATOR_PATHS.links,
      icon: LinkIcon,
    },
    {
      label: 'Storefront',
      description: 'Your public creator page',
      path: CREATOR_PATHS.storefront,
      icon: SparklesIcon,
    },
    {
      label: 'Products',
      description: 'Pick items to promote',
      path: CREATOR_PATHS.products,
      icon: ShoppingBagIcon,
    },
    {
      label: 'Campaigns',
      description: 'Brand collabs & bonuses',
      path: CREATOR_PATHS.campaigns,
      icon: MegaphoneIcon,
    },
    {
      label: 'Earnings',
      description: 'Balance & commission history',
      path: CREATOR_PATHS.earnings,
      icon: CurrencyDollarIcon,
    },
  ],
};

const SELLER_MENU: Omit<UserNavMenuConfig, 'persona' | 'personaLabel' | keyof typeof PERSONA_COPY.seller> = {
  hubLabel: 'Seller Dashboard',
  hubDescription: 'Store performance at a glance',
  hubPath: '/seller/dashboard',
  profileLabel: 'Seller profile',
  profileDescription: 'Store, payouts & preferences',
  profilePath: SELLER_SETTINGS_HREF,
  accent: 'seller',
  items: [
    {
      label: 'Overview',
      description: 'Sales snapshot & tasks',
      path: '/seller/dashboard',
      icon: HomeIcon,
    },
    {
      label: 'My store',
      description: 'Brand page & storefront',
      path: '/seller/store',
      icon: BuildingStorefrontIcon,
    },
    {
      label: 'Designs',
      description: 'Manage your product catalog',
      path: '/seller/designs',
      icon: ShoppingBagIcon,
    },
    {
      label: 'Orders',
      description: 'Fulfill buyer purchases',
      path: '/seller/orders',
      icon: ClipboardDocumentListIcon,
    },
    {
      label: 'Analytics',
      description: 'Traffic, conversion & trends',
      path: '/seller/analytics',
      icon: ChartBarIcon,
    },
    {
      label: 'Earnings & payouts',
      description: 'Wallet balance & withdrawals',
      path: '/seller/earnings',
      icon: CurrencyDollarIcon,
    },
  ],
};

const OWNER_MENU: Omit<UserNavMenuConfig, 'persona' | 'personaLabel' | keyof typeof PERSONA_COPY.owner> = {
  hubLabel: 'Command center',
  hubDescription: 'Platform health & priorities',
  hubPath: '/owner',
  profileLabel: 'Owner profile',
  profileDescription: 'System preferences & access',
  profilePath: '/owner/system/settings',
  accent: 'owner',
  items: [
    {
      label: 'Overview',
      description: 'KPIs, alerts & daily pulse',
      path: '/owner',
      icon: HomeIcon,
    },
    {
      label: 'Orders',
      description: 'Fulfillment pipeline & queues',
      path: '/owner/orders',
      icon: ClipboardDocumentListIcon,
    },
    {
      label: 'Products',
      description: 'Catalog, inventory & variants',
      path: '/owner/products',
      icon: ShoppingBagIcon,
    },
    {
      label: 'Revenue',
      description: 'Sales, refunds & pricing',
      path: '/owner/sales/revenue',
      icon: ChartBarIcon,
    },
    {
      label: 'Finance',
      description: 'Payouts, wallets & returns',
      path: '/owner/finance/payouts',
      icon: CurrencyDollarIcon,
    },
    {
      label: 'Customers',
      description: 'Segments, support & loyalty',
      path: '/owner/customers',
      icon: UserGroupIcon,
    },
  ],
};

const STAFF_MENU_ITEMS: UserNavMenuItem[] = [
  {
    label: 'Overview',
    description: 'Start here each shift',
    path: '/owner',
    icon: HomeIcon,
  },
  {
    label: 'Orders',
    description: 'Process & update order status',
    path: '/owner/orders',
    icon: ClipboardDocumentListIcon,
  },
  {
    label: 'Returns',
    description: 'Review refund requests',
    path: '/owner/return-requests',
    icon: TruckIcon,
  },
  {
    label: 'Print queue',
    description: 'Production & fulfillment',
    path: '/owner/shipping/print-queue',
    icon: PaintBrushIcon,
  },
  {
    label: 'Customers',
    description: 'Lookup buyers & support cases',
    path: '/owner/customers',
    icon: UserGroupIcon,
  },
  {
    label: 'Alerts',
    description: 'Operational issues to resolve',
    path: '/owner/alerts/operational',
    icon: BellAlertIcon,
  },
  {
    label: 'Users',
    description: 'Accounts, roles & access',
    path: '/owner/system/users',
    icon: UserIcon,
  },
];

const STAFF_ITEM_ACCESS: Partial<
  Record<string, (roles: UserRole[], overrides?: Record<string, boolean> | null) => boolean>
> = {
  '/owner': () => true,
  '/owner/orders': (roles, overrides) =>
    hasAnyPermission(
      roles,
      [Permission.ORDER_VIEW, Permission.ORDER_VIEW_ALL],
      overrides,
    ),
  '/owner/return-requests': (roles, overrides) =>
    hasAnyPermission(
      roles,
      [Permission.ORDER_REFUND, Permission.ORDER_CANCEL],
      overrides,
    ),
  '/owner/shipping/print-queue': (roles, overrides) =>
    hasPermission(roles, Permission.PRINT_VIEW, overrides),
  '/owner/customers': (roles, overrides) =>
    hasAnyPermission(
      roles,
      [Permission.USER_VIEW, Permission.USER_VIEW_ALL],
      overrides,
    ),
  '/owner/alerts/operational': (roles, overrides) =>
    hasAnyPermission(
      roles,
      [Permission.AUDIT_VIEW, Permission.REPORT_VIEW],
      overrides,
    ),
  '/owner/system/users': (roles, overrides) =>
    hasPermission(roles, Permission.USER_MANAGE_ROLES, overrides),
};

function buildStaffMenuItems(user: IUser): UserNavMenuItem[] {
  const { roles, permissionOverrides } = user;
  return STAFF_MENU_ITEMS.filter((item) => {
    const canAccess = STAFF_ITEM_ACCESS[item.path];
    return canAccess ? canAccess(roles, permissionOverrides) : true;
  });
}

const STAFF_MENU_BASE: Omit<
  UserNavMenuConfig,
  'persona' | 'personaLabel' | 'items' | keyof typeof PERSONA_COPY.staff
> = {
  hubLabel: 'Operations hub',
  hubDescription: 'Daily workflows & queues',
  hubPath: '/owner',
  profileLabel: 'My profile',
  profileDescription: 'Account details & security',
  profilePath: '/account/settings',
  accent: 'staff',
};

const PERSONA_MENUS: Record<
  NavPersona,
  Omit<UserNavMenuConfig, 'persona' | 'personaLabel' | keyof typeof PERSONA_COPY.buyer>
> = {
  buyer: BUYER_MENU,
  seller: SELLER_MENU,
  creator: CREATOR_MENU,
  owner: OWNER_MENU,
  staff: STAFF_MENU_BASE as Omit<
    UserNavMenuConfig,
    'persona' | 'personaLabel' | keyof typeof PERSONA_COPY.staff
  >,
};

export function buildUserNavMenuConfigForPersona(
  user: IUser,
  persona: NavPersona,
): UserNavMenuConfig {
  const base = PERSONA_MENUS[persona];
  const copy = PERSONA_COPY[persona];
  const items =
    persona === 'staff'
      ? buildStaffMenuItems(user)
      : base.items;

  return {
    persona,
    personaLabel: getPersonaLabel(persona, user.roles),
    greeting: copy.greeting,
    tagline: copy.tagline,
    spotlightLabel: copy.spotlightLabel,
    spotlightDescription: copy.spotlightDescription,
    spotlightPath: copy.spotlightPath,
    ...base,
    items,
  };
}

export function buildUserNavMenuConfig(user: IUser, pathname?: string): UserNavMenuConfig {
  return buildUserNavMenuConfigForPersona(user, resolveNavPersona(user.roles, pathname));
}

export type PersonaAccentTheme = {
  badge: string;
  header: string;
  headerGlow: string;
  hub: string;
  spotlight: string;
  spotlightIcon: string;
  icon: string;
  iconActive: string;
  ring: string;
  mobileHero: string;
  mobileHeroOrb: string;
  mobileHeroOrbSecondary: string;
  mobileAvatar: string;
  mobileBadge: string;
  mobileCta: string;
  avatarGradient: string;
  avatarGlow: string;
  personaDot: string;
  panelBg: string;
  panelBorder: string;
  panelFooter: string;
  rowActive: string;
  rowHover: string;
  textActive: string;
  chevron: string;
  chevronHover: string;
  profileIcon: string;
  profileIconHover: string;
  tileIdle: string;
  tileIdleBorder: string;
  tileHover: string;
  tileHoverBorder: string;
  dropdownShell: string;
  trigger: string;
  triggerHover: string;
};

export const PERSONA_ACCENT_STYLES: Record<
  UserNavMenuConfig['accent'],
  PersonaAccentTheme
> = {
  buyer: {
    badge: 'bg-purple-100 text-purple-800 ring-purple-200/60',
    header: 'from-purple-50 via-white to-pink-50 ring-purple-100/80',
    headerGlow: 'bg-purple-300/30',
    hub: 'from-[#3B3B98] to-[#9B59B6] shadow-purple-300/30',
    spotlight: 'border-purple-100 bg-purple-50/90 text-[#3B3B98] hover:bg-purple-50',
    spotlightIcon: 'bg-white/80 text-[#3B3B98]',
    icon: 'bg-purple-50/80 text-[#3B3B98]/70 group-hover:bg-purple-100 group-hover:text-[#3B3B98]',
    iconActive: 'bg-gradient-to-br from-[#3B3B98] to-[#9B59B6] text-white shadow-md shadow-purple-200/40',
    ring: 'ring-purple-200/60',
    mobileHero:
      'bg-gradient-to-br from-[#2d2d7a] via-[#3B3B98] to-[#9B59B6] shadow-[0_12px_40px_rgba(59,59,152,0.35)]',
    mobileHeroOrb: 'bg-white/20',
    mobileHeroOrbSecondary: 'bg-fuchsia-300/25',
    mobileAvatar: 'bg-white/15 ring-2 ring-white/35 backdrop-blur-md shadow-lg',
    mobileBadge: 'bg-white/20 text-white ring-1 ring-white/30',
    mobileCta:
      'bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-white/25',
    avatarGradient: 'from-[#3B3B98] to-[#9B59B6]',
    avatarGlow: 'from-[#3B3B98] to-[#9B59B6]',
    personaDot: 'bg-purple-500',
    panelBg: 'bg-gradient-to-b from-purple-50/80 via-purple-50/30 to-white',
    panelBorder: 'border-purple-100/70',
    panelFooter: 'bg-purple-50/40',
    rowActive: 'bg-purple-50/90',
    rowHover: 'hover:bg-purple-50/70',
    textActive: 'text-[#3B3B98]',
    chevron: 'text-purple-200/80',
    chevronHover: 'group-hover:text-purple-400',
    profileIcon: 'bg-purple-50 text-[#3B3B98]/80',
    profileIconHover: 'group-hover:bg-purple-100 group-hover:text-[#3B3B98]',
    tileIdle: 'bg-white/90',
    tileIdleBorder: 'border-purple-100/60',
    tileHover: 'hover:bg-purple-50/60',
    tileHoverBorder: 'hover:border-purple-200/80',
    dropdownShell:
      'border-purple-100/80 bg-gradient-to-b from-purple-50/50 to-white shadow-[0_24px_80px_rgba(59,59,152,0.16)]',
    trigger:
      'border-purple-200/80 bg-gradient-to-r from-purple-50 to-pink-50',
    triggerHover: 'hover:from-purple-100 hover:to-pink-100',
  },
  creator: {
    badge: 'bg-fuchsia-100 text-fuchsia-900 ring-fuchsia-200/70',
    header: 'from-fuchsia-50 via-white to-pink-50 ring-fuchsia-100/80',
    headerGlow: 'bg-fuchsia-300/25',
    hub: 'from-fuchsia-600 via-pink-600 to-rose-500 shadow-fuchsia-400/30',
    spotlight: 'border-fuchsia-200/80 bg-fuchsia-50/95 text-fuchsia-900 hover:bg-fuchsia-50',
    spotlightIcon: 'bg-white/80 text-fuchsia-700',
    icon: 'bg-fuchsia-50/90 text-fuchsia-700/80 group-hover:bg-fuchsia-100 group-hover:text-fuchsia-800',
    iconActive:
      'bg-gradient-to-br from-fuchsia-600 via-pink-600 to-rose-500 text-white shadow-md shadow-fuchsia-300/40',
    ring: 'ring-fuchsia-300/60',
    mobileHero:
      'bg-gradient-to-br from-fuchsia-900 via-fuchsia-700 to-pink-600 shadow-[0_12px_40px_rgba(192,38,211,0.32)]',
    mobileHeroOrb: 'bg-white/18',
    mobileHeroOrbSecondary: 'bg-pink-400/20',
    mobileAvatar: 'bg-white/15 ring-2 ring-white/35 backdrop-blur-md shadow-lg',
    mobileBadge: 'bg-white/20 text-white ring-1 ring-white/30',
    mobileCta:
      'bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-white/25',
    avatarGradient: 'from-fuchsia-600 via-pink-600 to-rose-500',
    avatarGlow: 'from-fuchsia-500 to-pink-500',
    personaDot: 'bg-fuchsia-500',
    panelBg: 'bg-gradient-to-b from-fuchsia-50/80 via-fuchsia-50/35 to-white',
    panelBorder: 'border-fuchsia-100/70',
    panelFooter: 'bg-fuchsia-50/45',
    rowActive: 'bg-fuchsia-50/90',
    rowHover: 'hover:bg-fuchsia-50/70',
    textActive: 'text-fuchsia-800',
    chevron: 'text-fuchsia-200/90',
    chevronHover: 'group-hover:text-fuchsia-500',
    profileIcon: 'bg-fuchsia-50 text-fuchsia-700',
    profileIconHover: 'group-hover:bg-fuchsia-100 group-hover:text-fuchsia-900',
    tileIdle: 'bg-white/90',
    tileIdleBorder: 'border-fuchsia-100/70',
    tileHover: 'hover:bg-fuchsia-50/65',
    tileHoverBorder: 'hover:border-fuchsia-200/80',
    dropdownShell:
      'border-fuchsia-100/80 bg-gradient-to-b from-fuchsia-50/55 to-white shadow-[0_24px_80px_rgba(192,38,211,0.14)]',
    trigger:
      'border-fuchsia-200/80 bg-gradient-to-r from-fuchsia-50 to-pink-50',
    triggerHover: 'hover:from-fuchsia-100 hover:to-pink-100',
  },
  seller: {
    badge: 'bg-violet-100 text-violet-900 ring-violet-200/70',
    header: 'from-violet-50 via-white to-purple-50 ring-violet-100/80',
    headerGlow: 'bg-violet-300/25',
    hub: 'from-[#553C9A] via-[#7C3AED] to-[#9333EA] shadow-violet-400/30',
    spotlight: 'border-violet-200/80 bg-violet-50/95 text-violet-900 hover:bg-violet-50',
    spotlightIcon: 'bg-white/80 text-violet-700',
    icon: 'bg-violet-50/90 text-violet-700/80 group-hover:bg-violet-100 group-hover:text-violet-800',
    iconActive:
      'bg-gradient-to-br from-[#553C9A] via-[#7C3AED] to-[#9333EA] text-white shadow-md shadow-violet-300/40',
    ring: 'ring-violet-300/60',
    mobileHero:
      'bg-gradient-to-br from-[#44337a] via-[#6D28D9] to-[#9333EA] shadow-[0_12px_40px_rgba(109,40,217,0.32)]',
    mobileHeroOrb: 'bg-white/18',
    mobileHeroOrbSecondary: 'bg-fuchsia-400/20',
    mobileAvatar: 'bg-white/15 ring-2 ring-white/35 backdrop-blur-md shadow-lg',
    mobileBadge: 'bg-white/20 text-white ring-1 ring-white/30',
    mobileCta:
      'bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-white/25',
    avatarGradient: 'from-[#553C9A] via-[#7C3AED] to-[#9333EA]',
    avatarGlow: 'from-[#6D28D9] to-[#9333EA]',
    personaDot: 'bg-violet-500',
    panelBg: 'bg-gradient-to-b from-violet-50/80 via-violet-50/35 to-white',
    panelBorder: 'border-violet-100/70',
    panelFooter: 'bg-violet-50/45',
    rowActive: 'bg-violet-50/90',
    rowHover: 'hover:bg-violet-50/70',
    textActive: 'text-violet-800',
    chevron: 'text-violet-200/90',
    chevronHover: 'group-hover:text-violet-500',
    profileIcon: 'bg-violet-50 text-violet-700',
    profileIconHover: 'group-hover:bg-violet-100 group-hover:text-violet-900',
    tileIdle: 'bg-white/90',
    tileIdleBorder: 'border-violet-100/70',
    tileHover: 'hover:bg-violet-50/65',
    tileHoverBorder: 'hover:border-violet-200/80',
    dropdownShell:
      'border-violet-100/80 bg-gradient-to-b from-violet-50/55 to-white shadow-[0_24px_80px_rgba(109,40,217,0.14)]',
    trigger:
      'border-violet-200/80 bg-gradient-to-r from-violet-50 to-purple-50',
    triggerHover: 'hover:from-violet-100 hover:to-purple-100',
  },
  owner: {
    badge: 'bg-indigo-100 text-indigo-800 ring-indigo-200/60',
    header: 'from-indigo-50 via-white to-violet-50 ring-indigo-100/80',
    headerGlow: 'bg-indigo-300/25',
    hub: 'from-indigo-700 via-indigo-600 to-violet-600 shadow-indigo-400/30',
    spotlight: 'border-indigo-100 bg-indigo-50/90 text-indigo-900 hover:bg-indigo-50',
    spotlightIcon: 'bg-white/80 text-indigo-700',
    icon: 'bg-indigo-50/90 text-indigo-700/80 group-hover:bg-indigo-100 group-hover:text-indigo-800',
    iconActive:
      'bg-gradient-to-br from-indigo-700 to-violet-600 text-white shadow-md shadow-indigo-300/40',
    ring: 'ring-indigo-200/60',
    mobileHero:
      'bg-gradient-to-br from-indigo-950 via-indigo-700 to-violet-600 shadow-[0_12px_40px_rgba(67,56,202,0.35)]',
    mobileHeroOrb: 'bg-white/12',
    mobileHeroOrbSecondary: 'bg-violet-300/20',
    mobileAvatar: 'bg-white/15 ring-2 ring-white/35 backdrop-blur-md shadow-lg',
    mobileBadge: 'bg-white/20 text-white ring-1 ring-white/30',
    mobileCta:
      'bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-white/25',
    avatarGradient: 'from-indigo-600 to-violet-600',
    avatarGlow: 'from-indigo-500 to-violet-500',
    personaDot: 'bg-indigo-500',
    panelBg: 'bg-gradient-to-b from-indigo-50/80 via-indigo-50/30 to-white',
    panelBorder: 'border-indigo-100/70',
    panelFooter: 'bg-indigo-50/40',
    rowActive: 'bg-indigo-50/90',
    rowHover: 'hover:bg-indigo-50/70',
    textActive: 'text-indigo-800',
    chevron: 'text-indigo-200/90',
    chevronHover: 'group-hover:text-indigo-500',
    profileIcon: 'bg-indigo-50 text-indigo-700',
    profileIconHover: 'group-hover:bg-indigo-100 group-hover:text-indigo-900',
    tileIdle: 'bg-white/90',
    tileIdleBorder: 'border-indigo-100/60',
    tileHover: 'hover:bg-indigo-50/60',
    tileHoverBorder: 'hover:border-indigo-200/80',
    dropdownShell:
      'border-indigo-100/80 bg-gradient-to-b from-indigo-50/50 to-white shadow-[0_24px_80px_rgba(67,56,202,0.14)]',
    trigger:
      'border-indigo-200/80 bg-gradient-to-r from-indigo-50 to-violet-50',
    triggerHover: 'hover:from-indigo-100 hover:to-violet-100',
  },
  staff: {
    badge: 'bg-slate-100 text-slate-700 ring-slate-200/70',
    header: 'from-slate-50 via-white to-indigo-50/60 ring-slate-200/80',
    headerGlow: 'bg-slate-300/20',
    hub: 'from-slate-800 via-slate-700 to-indigo-700 shadow-slate-500/30',
    spotlight: 'border-slate-200 bg-slate-50/95 text-slate-800 hover:bg-slate-100',
    spotlightIcon: 'bg-white/80 text-slate-700',
    icon: 'bg-slate-100/90 text-slate-600 group-hover:bg-slate-200 group-hover:text-slate-900',
    iconActive:
      'bg-gradient-to-br from-slate-700 to-indigo-700 text-white shadow-md shadow-slate-400/35',
    ring: 'ring-slate-300/60',
    mobileHero:
      'bg-gradient-to-br from-slate-900 via-slate-700 to-indigo-800 shadow-[0_12px_40px_rgba(30,41,59,0.38)]',
    mobileHeroOrb: 'bg-white/10',
    mobileHeroOrbSecondary: 'bg-indigo-300/15',
    mobileAvatar: 'bg-white/15 ring-2 ring-white/35 backdrop-blur-md shadow-lg',
    mobileBadge: 'bg-white/20 text-white ring-1 ring-white/30',
    mobileCta:
      'bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-white/25',
    avatarGradient: 'from-slate-700 to-indigo-700',
    avatarGlow: 'from-slate-600 to-indigo-600',
    personaDot: 'bg-slate-600',
    panelBg: 'bg-gradient-to-b from-slate-50/90 via-slate-50/40 to-white',
    panelBorder: 'border-slate-200/80',
    panelFooter: 'bg-slate-50/60',
    rowActive: 'bg-slate-100/90',
    rowHover: 'hover:bg-slate-50/90',
    textActive: 'text-slate-900',
    chevron: 'text-slate-300',
    chevronHover: 'group-hover:text-indigo-400',
    profileIcon: 'bg-slate-100 text-slate-600',
    profileIconHover: 'group-hover:bg-slate-200 group-hover:text-slate-900',
    tileIdle: 'bg-white/90',
    tileIdleBorder: 'border-slate-200/70',
    tileHover: 'hover:bg-slate-50/80',
    tileHoverBorder: 'hover:border-slate-300/80',
    dropdownShell:
      'border-slate-200/80 bg-gradient-to-b from-slate-50/60 to-white shadow-[0_24px_80px_rgba(30,41,59,0.12)]',
    trigger:
      'border-slate-200/80 bg-gradient-to-r from-slate-50 to-indigo-50/60',
    triggerHover: 'hover:from-slate-100 hover:to-indigo-50',
  },
};
